import { useEffect, useState } from "react";
import { SeleccionarTodosCheckbox } from "@/components/SeleccionarTodosCheckbox";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, AlertCircle, ChevronRight, Info } from "lucide-react";
import { api } from "@/lib/api";
import { useEsInternoNpmx } from "@/lib/sesion";

interface TransicionDisponible {
  id: string;
  codigo: string;
  nombre: string;
  estadoDestinoId: string;
  estadoDestinoCodigo: string;
  requiereMotivo: boolean;
  reglaCodigo: string | null;
}
interface EstadoActual {
  instanciaId: string;
  flujoId: string;
  estado: {
    id: string;
    codigo: string;
    nombre: string;
    esFinal: boolean;
    slaDias: number | null;
  };
  cerrada: boolean;
  transicionesDisponibles: TransicionDisponible[];
  /** Roles (sin ROLE_) que habilitan acciones en la etapa actual — para el mensaje cuando no hay acciones. */
  rolesConAcciones?: string[];
}

/**
 * Modal para avanzar una solicitud Telecom: lista las transiciones disponibles
 * desde el estado actual (ya filtradas por roles del actor en el backend), pide
 * motivo cuando aplica y dispara el wrapper sectorial.
 */
export function TransitionsModal({
  open,
  solicitudId,
  instanciaId,
  folio,
  sector = "telecom",
  onClose,
  onDone,
}: {
  open: boolean;
  solicitudId: string;
  instanciaId: string;
  folio: string;
  /** Código de sector para construir los endpoints (telecom | seguridad | …). */
  sector?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const esInterno = useEsInternoNpmx();
  const base = `/api/sector/${sector}/solicitudes`;
  // El Número de Referencia (UPT) en PAGO_VALIDADO solo aplica al flujo Telecom.
  const usaNumReferencia = sector === "telecom";
  const [selected, setSelected] = useState<TransicionDisponible | null>(null);
  const [motivo, setMotivo] = useState("");
  const [numReferencia, setNumReferencia] = useState("");
  const [notificarA, setNotificarA] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["estado-actual", instanciaId],
    queryFn: async () => (await api.get<EstadoActual>(`/api/flujos/instancias/${instanciaId}`)).data,
    enabled: open && !!instanciaId,
    // staleTime=0 garantiza refetch al reabrir tras una transición previa.
    staleTime: 0,
  });

  interface DestinatarioInterno { id: string; correo: string; nombre: string; }
  interface DestinatarioRelacionado extends DestinatarioInterno {
    tipo: string;
    rlRazonSocial: string;
    /** «Representante legal» o «Usuario autorizado». */
    relacion?: string;
  }
  const qDestinatarios = useQuery<{
    internos: DestinatarioInterno[];
    relacionados: DestinatarioRelacionado[];
    // RF-02: destinatarios recordados de la empresa (ya filtrados a vigentes).
    preseleccionados?: string[];
  }>({
    queryKey: ["destinatarios-notificacion", solicitudId],
    queryFn: async () =>
      (await api.get(`${base}/${solicitudId}/destinatarios-notificacion`)).data,
    enabled: open && !!solicitudId,
  });

  // RF-02: al cargar los destinatarios, sembrar la selección con los recordados
  // de la empresa (editable). Solo mientras el usuario no haya tocado la lista,
  // para no pisar sus cambios en re-renders.
  const [tocado, setTocado] = useState(false);
  useEffect(() => {
    if (tocado) return;
    const pre = qDestinatarios.data?.preseleccionados;
    if (pre && pre.length > 0) setNotificarA(pre);
  }, [qDestinatarios.data, tocado]);

  if (!open) return null;

  const reset = () => {
    setSelected(null);
    setMotivo("");
    setNumReferencia("");
    setNotificarA([]);
    setTocado(false);
    setError(null);
  };

  const toggleTodosNotificar = (ids: string[], marcar: boolean) => {
    setTocado(true);
    setNotificarA((prev) => (marcar
      ? Array.from(new Set([...prev, ...ids]))
      : prev.filter((x) => !ids.includes(x))));
  };

  const toggleNotificar = (id: string) => {
    setTocado(true);
    setNotificarA((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };
  const ejecutar = async (t: TransicionDisponible) => {
    if (t.requiereMotivo && !motivo.trim()) {
      setError("Esta transición requiere motivo.");
      return;
    }
    // Pantalla "Facturación" del portlet legacy: Finanzas captura el Número
    // de Referencia (UPT{AA}-{NNNN}) al validar el pago. Es el folio que
    // relaciona la solicitud con su sistema de facturación interno y el
    // mismo que se imprime en el PDF como "número de Referencia".
    if (usaNumReferencia && t.codigo === "PAGO_VALIDADO" && !numReferencia.trim()) {
      setError("Captura el número de Referencia (UPT) asignado por Finanzas.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        motivo: motivo.trim() || null,
      };
      if (usaNumReferencia && t.codigo === "PAGO_VALIDADO") {
        payload.numReferencia = numReferencia.trim().toUpperCase();
      }
      if (notificarA.length > 0) {
        payload.notificarA = notificarA;
      }
      await api.post(
        `${base}/${solicitudId}/transiciones/${t.codigo}`,
        { motivo: payload.motivo, payload },
      );
      // Invalida queries que dependen del estado actual para evitar el bug de
      // "botón duplicado": tras una transición exitosa el botón debe reflejar
      // las transiciones del nuevo estado, no quedarse con las del anterior.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["estado-actual", instanciaId] }),
        qc.invalidateQueries({ queryKey: ["destinatarios-notificacion", solicitudId] }),
        qc.invalidateQueries({ queryKey: [`solicitud-${sector}`, solicitudId] }),
      ]);
      reset();
      onDone();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data?.detail ||
        (e as { response?: { data?: { title?: string } } }).response?.data?.title ||
        (e as Error).message ||
        "Error inesperado.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-full w-full max-w-lg flex-col rounded-lg bg-surface shadow-overlay">
        <div className="flex shrink-0 items-start justify-between border-b px-5 py-3">
          <div>
            {/* Al externo no se le habla de «avanzar» ni se le enseña el código
                interno del estado: no puede accionar y solo genera confusión. */}
            <h2 className="text-base font-semibold text-fg">
              {esInterno ? "Avanzar solicitud" : "Estado de la solicitud"}
            </h2>
            <p className="mt-0.5 text-xs text-fg-subtle">
              <span className="font-mono">{folio}</span>
              {q.data && (
                <>
                  {" · etapa "}
                  <span className="font-medium">
                    {esInterno ? q.data.estado.codigo : q.data.estado.nombre}
                  </span>
                </>
              )}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="rounded p-1 text-fg-faint hover:bg-surface-2 hover:text-fg-muted"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {q.isLoading && <p className="text-sm text-fg-subtle">Cargando transiciones…</p>}
          {q.error && (
            <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
              <AlertCircle className="mt-0.5 h-4 w-4" />
              <span>No se pudo cargar el estado.</span>
            </div>
          )}
          {q.data && q.data.cerrada && (
            <div className="rounded bg-surface-2 p-3 text-sm text-fg-muted">
              Esta instancia está cerrada — no hay transiciones disponibles.
            </div>
          )}
          {/*
            Sin acciones disponibles. El texto cambia según quién mira:

            - Interno de NPMX: le sirve saber qué rol habilita la etapa y que
              puede pedir que se le asigne, porque puede accionar sobre ella.
            - Externo (cliente o gestoría): enumerarle roles internos se lee
              como un error de permisos suyo, cuando en realidad su solicitud
              está bien y el turno es de NPMX. Reportado por Certificaciones MHR
              en NPMX-S-2026-0029, etapa «Solicitud».
          */}
          {q.data && !q.data.cerrada && q.data.transicionesDisponibles.length === 0 && (
            esInterno ? (
              <div className="space-y-1 rounded bg-estado-warn-bg p-3 text-sm text-estado-warn">
                <div className="font-medium">
                  No tienes acciones disponibles en la etapa «{q.data.estado.nombre}».
                </div>
                <div className="text-xs">
                  {q.data.rolesConAcciones?.length
                    ? <>Las acciones de esta etapa requieren el rol{" "}
                        <span className="font-mono">{q.data.rolesConAcciones.join(", ")}</span>{" "}
                        o ser el <strong>responsable asignado</strong> de la etapa.</>
                    : "Esta etapa no tiene acciones configuradas."}
                  {" "}Si debes atender esta solicitud, pide a un coordinador que te la asigne.
                </div>
              </div>
            ) : (
              <div className="space-y-1 rounded bg-estado-info-bg p-3 text-sm text-fg">
                <div className="font-medium">
                  Tu solicitud está en revisión por NPMX.
                </div>
                {/* Deliberadamente NO se promete un correo: hoy no hay ni una
                    sola notificación activa en kernel.flujo_transicion_notificacion
                    (Seguridad tiene 0 y las 3 de Telecom están inactivas). */}
                <div className="text-xs text-fg-muted">
                  Se encuentra en la etapa «{q.data.estado.nombre}» y no requiere
                  ninguna acción de tu parte por ahora. Si NPMX necesita algo,
                  la solicitud regresará a una etapa que podrás atender desde
                  esta misma pantalla.
                </div>
              </div>
            )
          )}
          {q.data && q.data.transicionesDisponibles.length > 0 && (
            <ul className="space-y-1.5">
              {q.data.transicionesDisponibles.map((t) => {
                const isSelected = selected?.id === t.id;
                return (
                  <li key={t.id}>
                    <button
                      onClick={() => setSelected(t)}
                      className={`flex w-full items-start gap-2 rounded border px-3 py-2 text-left text-sm transition ${
                        isSelected
                          ? "border-primary-strong bg-estado-info-bg"
                          : "border-edge hover:border-edge-strong hover:bg-surface-2"
                      }`}
                      disabled={loading}
                    >
                      <ChevronRight className="mt-0.5 h-4 w-4 text-fg-faint" />
                      <div className="flex-1">
                        <div className="font-medium text-fg">{t.nombre}</div>
                        <div className="mt-0.5 text-xs text-fg-subtle">
                          <span className="font-mono">{t.codigo}</span>
                          {" → "}
                          <span className="font-mono">{t.estadoDestinoCodigo}</span>
                          {t.requiereMotivo && (
                            <span className="ml-2 rounded bg-estado-warn-bg px-1.5 py-0.5 text-xs font-medium text-estado-warn">
                              motivo obligatorio
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {selected && (
            <div className="space-y-2 border-t pt-3">
              {usaNumReferencia && selected.codigo === "PAGO_VALIDADO" && (
                <div className="space-y-1 rounded border border-estado-ok/25 bg-estado-ok-bg p-3">
                  <label className="text-xs uppercase tracking-wide text-estado-ok">
                    Número de referencia bancaria <span className="text-estado-err">*</span>
                  </label>
                  <input
                    type="text"
                    value={numReferencia}
                    onChange={(e) => setNumReferencia(e.target.value.toUpperCase())}
                    maxLength={150}
                    placeholder="Ej. BNX-2026-987654"
                    className="w-full rounded border border-edge-strong px-2 py-1.5 text-sm uppercase focus:border-primary focus:outline-none"
                    disabled={loading}
                  />
                  <p className="text-xs text-estado-ok">
                    Queda almacenado en la solicitud como evidencia del pago validado.
                  </p>
                </div>
              )}
              <label className="text-xs uppercase tracking-wide text-fg-subtle">
                Motivo {selected.requiereMotivo && <span className="text-estado-err">*</span>}
              </label>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={2}
                className="w-full rounded border border-edge-strong px-2 py-1 text-sm focus:border-primary focus:outline-none"
                placeholder={selected.requiereMotivo ? "Obligatorio" : "Opcional"}
                disabled={loading}
              />

              {/* Multiselect de destinatarios */}
              <div className="rounded border bg-surface">
                <div className="border-b px-2 py-1.5 text-xs uppercase tracking-wide text-fg-subtle">
                  Notificar por correo a ({notificarA.length} seleccionado{notificarA.length === 1 ? "" : "s"})
                </div>
                {/* RF-02: la selección se recuerda por empresa para futuras solicitudes. */}
                <div className="flex items-start gap-1.5 border-b bg-estado-info-bg px-2 py-1.5 text-[11px] leading-snug text-estado-info">
                  <Info className="mt-px h-3.5 w-3.5 shrink-0" />
                  <span>Los destinatarios seleccionados quedarán guardados para futuras solicitudes de esta empresa. Puedes modificarlos en cualquier momento.</span>
                </div>
                <div className="max-h-56 overflow-y-auto p-2 text-sm">
                  {qDestinatarios.isLoading && (
                    <div className="text-xs text-fg-subtle">Cargando destinatarios…</div>
                  )}
                  {qDestinatarios.data && (
                    <>
                      {qDestinatarios.data.internos.length > 0 && (
                        <>
                          <div className="mt-1 mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">
                            Internos NPMX
                            <SeleccionarTodosCheckbox
                              className="ml-auto"
                              ids={qDestinatarios.data.internos.map((u) => u.id)}
                              seleccionados={notificarA}
                              onToggleTodos={toggleTodosNotificar}
                            />
                          </div>
                          {qDestinatarios.data.internos.map((u) => (
                            <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-surface-2">
                              <input
                                type="checkbox"
                                checked={notificarA.includes(u.id)}
                                onChange={() => toggleNotificar(u.id)}
                              />
                              <span className="font-medium">{u.nombre || u.correo}</span>
                              <span className="text-fg-subtle">— {u.correo}</span>
                            </label>
                          ))}
                        </>
                      )}
                      {qDestinatarios.data.relacionados.length > 0 && (
                        <>
                          <div className="mt-3 mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">
                            Solicitante y empresa tramitadora
                            <SeleccionarTodosCheckbox
                              className="ml-auto"
                              ids={qDestinatarios.data.relacionados.map((u) => u.id)}
                              seleccionados={notificarA}
                              onToggleTodos={toggleTodosNotificar}
                            />
                          </div>
                          {qDestinatarios.data.relacionados.map((u) => (
                            <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-surface-2">
                              <input
                                type="checkbox"
                                checked={notificarA.includes(u.id)}
                                onChange={() => toggleNotificar(u.id)}
                              />
                              <span className="font-medium">{u.nombre || u.correo}</span>
                              <span className="text-fg-subtle">— {u.correo}</span>
                              <span className="ml-auto text-[10px] text-fg-faint">{u.relacion ?? u.tipo} · {u.rlRazonSocial}</span>
                            </label>
                          ))}
                        </>
                      )}
                      {qDestinatarios.data.internos.length === 0 && qDestinatarios.data.relacionados.length === 0 && (
                        <div className="text-xs text-fg-subtle">No hay destinatarios disponibles.</div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
                  <AlertCircle className="mt-0.5 h-4 w-4" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t bg-surface-2 px-5 py-3">
          <button
            onClick={handleClose}
            className="rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            onClick={() => selected && ejecutar(selected)}
            className="rounded bg-primary px-3 py-1.5 text-sm text-white hover:opacity-90 disabled:opacity-50"
            disabled={!selected || loading}
          >
            {loading ? "Ejecutando…" : "Ejecutar"}
          </button>
        </div>
      </div>
    </div>
  );
}
